import type { GlossaryTerms, SanityBlockContent } from "../../../api/spark";

export const GLOSSARY_MARK_TYPE = "glossaryTerm";

// A char counts as part of a word for boundary checks if it is a Unicode letter
// or number. This keeps German umlauts (ä/ö/ü), ß and accented letters as word
// chars so e.g. "Öko" does not match inside "Ökologie" — JS `\b` would not.
const WORD_CHAR = /[\p{L}\p{N}]/u;
const isWordChar = (ch: string | undefined): boolean =>
  !!ch && WORD_CHAR.test(ch);

// A matchable token: the literal string to look for (`match`, a canonical term
// or one of its aliases) plus the `canonical` term it resolves to. The mark
// always carries the canonical term, and "first occurrence" is tracked per
// canonical term so a term and its aliases share one highlight.
type Term = {
  match: string;
  lower: string;
  len: number;
  canonical: string;
  canonicalLower: string;
};

// Portable Text is loosely typed here on purpose: we only touch the few fields
// we care about and pass everything else through untouched.
type PtSpan = {
  _type: "span";
  _key?: string;
  text: string;
  marks?: string[];
  [key: string]: unknown;
};

type PtMarkDef = { _key: string; _type: string; [key: string]: unknown };

type PtBlock = {
  _type: string;
  _key?: string;
  children?: unknown[];
  markDefs?: PtMarkDef[];
  content?: unknown[];
  branches?: { content?: unknown[]; [key: string]: unknown }[];
  [key: string]: unknown;
};

/**
 * Walks a Portable Text tree and wraps the first occurrence (per step, in
 * document order) of each glossary term in a custom `glossaryTerm` annotation.
 *
 * Matching is case-insensitive, whole-word (Unicode-aware boundaries) and
 * prefers the longest term at any given position. Existing marks (e.g. links)
 * are preserved; the glossary mark is layered on top so it nests cleanly.
 *
 * Recurses into the nested rich text held by `quote`, `styledBox` and `branch`
 * blocks so the "first occurrence" set stays coherent across the whole step.
 * Matches never span across span boundaries (span edges count as word edges).
 *
 * `terms` maps each canonical term to its aliases (alternative spellings/forms).
 * Both the canonical term and its aliases are matched, but the resulting mark
 * always carries the canonical term so tooltips resolve to a single entry.
 *
 * Returns the marked-up content alongside `terms`: the sorted, unique set of
 * canonical terms that were actually marked (original casing, as stored). The
 * caller uses this to fetch just those terms' descriptions for tooltips.
 */
export function injectGlossaryMarks(
  content: SanityBlockContent,
  terms: GlossaryTerms,
): { content: SanityBlockContent; terms: string[] } {
  const entries = terms ? Object.entries(terms) : [];
  if (!content?.length || !entries.length) return { content, terms: [] };
  // Expand each canonical term + its aliases into matchable tokens, longest
  // first so multi-word terms win over their prefixes at a position.
  const sorted: Term[] = entries
    .flatMap(([canonical, aliases]) =>
      [canonical, ...(aliases || [])].map((match) => ({
        match,
        lower: match.toLowerCase(),
        len: match.length,
        canonical,
        canonicalLower: canonical.toLowerCase(),
      })),
    )
    .filter((t) => t.match && t.match.length > 0)
    .sort((a, b) => b.len - a.len);
  if (!sorted.length) return { content, terms: [] };

  const seen = new Set<string>();
  // Canonical terms actually marked, in original casing, for the caller to look
  // up descriptions. `seen` already guarantees one mark per canonical term.
  const found = new Set<string>();
  let keyCounter = 0;
  const nextKey = () => `gloss-${keyCounter++}`;

  const processed = processBlocks(
    content as unknown as PtBlock[],
    sorted,
    seen,
    found,
    nextKey,
  ) as unknown as SanityBlockContent;
  return { content: processed, terms: [...found].sort() };
}

function processBlocks(
  blocks: PtBlock[],
  terms: Term[],
  seen: Set<string>,
  found: Set<string>,
  nextKey: () => string,
): PtBlock[] {
  return blocks.map((block) => processNode(block, terms, seen, found, nextKey));
}

function processNode(
  node: PtBlock,
  terms: Term[],
  seen: Set<string>,
  found: Set<string>,
  nextKey: () => string,
): PtBlock {
  if (!node || typeof node !== "object") return node;
  switch (node._type) {
    case "block":
      return Array.isArray(node.children)
        ? processTextBlock(node, terms, seen, found, nextKey)
        : node;
    case "quote":
    case "styledBox":
      return Array.isArray(node.content)
        ? {
            ...node,
            content: processBlocks(
              node.content as PtBlock[],
              terms,
              seen,
              found,
              nextKey,
            ),
          }
        : node;
    case "branch":
      return Array.isArray(node.branches)
        ? {
            ...node,
            branches: node.branches.map((b) =>
              b && Array.isArray(b.content)
                ? {
                    ...b,
                    content: processBlocks(
                      b.content as PtBlock[],
                      terms,
                      seen,
                      found,
                      nextKey,
                    ),
                  }
                : b,
            ),
          }
        : node;
    default:
      return node;
  }
}

function processTextBlock(
  block: PtBlock,
  terms: Term[],
  seen: Set<string>,
  found: Set<string>,
  nextKey: () => string,
): PtBlock {
  const markDefs: PtMarkDef[] = Array.isArray(block.markDefs)
    ? [...block.markDefs]
    : [];
  const children: unknown[] = [];
  let changed = false;

  for (const child of block.children as unknown[]) {
    const span = child as PtSpan;
    if (
      !span ||
      span._type !== "span" ||
      typeof span.text !== "string" ||
      !span.text
    ) {
      children.push(child);
      continue;
    }
    const split = scanSpan(span, terms, seen, found, markDefs, nextKey);
    if (split.length === 1 && split[0] === span) {
      children.push(span);
    } else {
      changed = true;
      children.push(...split);
    }
  }

  if (!changed) return block;
  return { ...block, children, markDefs };
}

function scanSpan(
  span: PtSpan,
  terms: Term[],
  seen: Set<string>,
  found: Set<string>,
  markDefs: PtMarkDef[],
  nextKey: () => string,
): PtSpan[] {
  const { text } = span;
  const baseMarks = Array.isArray(span.marks) ? span.marks : [];
  const result: PtSpan[] = [];
  let last = 0;
  let i = 0;

  const pushPlain = (from: number, to: number) => {
    if (to <= from) return;
    result.push({
      ...span,
      // Reuse the original key for the leading chunk, fresh keys otherwise so
      // sibling spans stay unique for React reconciliation.
      _key: from === 0 ? span._key : nextKey(),
      text: text.slice(from, to),
    });
  };

  while (i < text.length) {
    let matched: Term | null = null;
    for (const term of terms) {
      const end = i + term.len;
      if (end > text.length) continue;
      if (text.slice(i, end).toLowerCase() !== term.lower) continue;
      // Whole-word: a word char on either side disqualifies the match.
      if (isWordChar(text[i - 1])) continue;
      if (isWordChar(text[end])) continue;
      // "Seen" is tracked per canonical term so a term and its aliases share a
      // single highlight (the first occurrence of either, in document order).
      if (seen.has(term.canonicalLower)) continue;
      matched = term; // longest-first ordering means the first hit wins
      break;
    }

    if (matched) {
      pushPlain(last, i);
      const markKey = nextKey();
      markDefs.push({
        _key: markKey,
        _type: GLOSSARY_MARK_TYPE,
        term: matched.canonical,
      });
      const end = i + matched.len;
      result.push({
        ...span,
        _key: nextKey(),
        text: text.slice(i, end),
        marks: [...baseMarks, markKey],
      });
      seen.add(matched.canonicalLower);
      found.add(matched.canonical);
      i = end;
      last = end;
    } else {
      i++;
    }
  }

  if (last === 0) return [span]; // no matches — leave the span untouched
  pushPlain(last, text.length);
  return result;
}
